import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const useFetchauthApis = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BASE_URL;

  // Track controller for canceling on unmount
  const abortControllerRef = useRef(null);

  const axiosInstance = axios.create({
    baseURL: API_URL,
  });

  const fetchData = useCallback(
    async ({ url = "", method = "get", payload = null }) => {
      setLoading(true);
      setApiError(null);

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        axiosInstance.defaults.headers.restype = "json";
        const config = {
          url,
          method,
          signal: controller.signal,
        };

        if (payload) {
          config.data = payload;
        }

        const { data: response } = await axiosInstance.request(config);
        setData(response);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (axios.isCancel(error)) {
          console.log("Request was canceled");
          return;
        }
        if (error?.code === "ERR_CANCELED") {
          console.log("🚫 Request cancelled due to component unmount.");
          return;
        }

        if (error?.response?.data?.message) {
          return setApiError(error?.response?.data?.message);
        }
        if (error?.response?.data?.error) {
          return setApiError(error?.response?.data?.error);
        }
        if ([401, 402, 403].includes(error?.response?.status)) {
          navigate("/");
          return;
        }
        if (
          error?.response?.status?.toString()?.startsWith("5") ||
          error?.message?.includes("Network Error")
        ) {
          toast.error("We can't reach our network right now.", {
            position: "bottom-center",
            autoClose: 10000,
          });
          return;
        }

        setApiError(`Error: ${error?.message}`);
        return error;
      } finally {
        setLoading(false);
      }
    },
    [axiosInstance, navigate]
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { loading, data, apiError, fetchData };
};
